<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-header">
        <h1 class="auth-title">회원가입</h1>
        <p class="auth-subtitle">가입하고 {{ formatPoints(settings.signupBonus) }} 포인트 받으세요!</p>
      </div>

      <!-- 가입 보너스 안내 -->
      <div class="bonus-banner">
        <div class="bonus-icon">🎁</div>
        <div class="bonus-info">
          <span class="bonus-title">신규 가입 혜택</span>
          <span class="bonus-amount">{{ formatPoints(settings.signupBonus) }}P 즉시 지급</span>
        </div>
      </div>

      <button @click="handleGoogleRegister" class="btn-google" :disabled="isLoading">
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Google로 시작하기
      </button>

      <div class="divider">
        <span>또는 이메일로 가입</span>
      </div>

      <form @submit.prevent="handleEmailRegister" class="auth-form">
        <div class="input-group">
          <label for="email">이메일</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            placeholder="이메일을 입력하세요"
            required
          />
        </div>

        <div class="input-group">
          <label for="password">비밀번호</label>
          <input 
            type="password" 
            id="password" 
            v-model="password" 
            placeholder="6자 이상 입력하세요"
            required
          />
        </div>

        <div class="input-group">
          <label for="passwordConfirm">비밀번호 확인</label>
          <input 
            type="password" 
            id="passwordConfirm" 
            v-model="passwordConfirm" 
            placeholder="비밀번호를 다시 입력하세요"
            required
          />
        </div>

        <!-- 추천인 코드 입력 -->
        <div class="input-group">
          <label for="referralCode">
            추천인 코드 
            <span class="label-optional">(선택)</span>
          </label>
          <div class="referral-input-wrapper">
            <input 
              type="text" 
              id="referralCode" 
              v-model="referralCode" 
              placeholder="추천인 코드 6자리"
              maxlength="6"
              @input="handleReferralInput"
              @blur="validateReferralCodeInput"
              :class="{ 'input-success': referralValid, 'input-error': referralError }"
            />
            <span v-if="referralChecking" class="referral-status checking">확인 중...</span>
            <span v-else-if="referralValid" class="referral-status valid">✓</span>
            <span v-else-if="referralError" class="referral-status invalid">✕</span>
          </div>
          <p v-if="referralMessage" :class="['referral-message', referralValid ? 'success' : 'error']">
            {{ referralMessage }}
          </p>
          <p v-if="referralValid" class="referral-bonus">
            🎉 추천인 코드 적용 시 추가 {{ formatPoints(settings.refereeBonus) }}P 지급!
          </p>
        </div>

        <p v-if="error" class="error-message">{{ error }}</p>
        <p v-if="localError" class="error-message">{{ localError }}</p>

        <button type="submit" class="btn-primary" :disabled="isLoading">
          {{ isLoading ? '가입 중...' : '가입하기' }}
        </button>
      </form>

      <p class="auth-link">
        이미 계정이 있으신가요? 
        <router-link to="/login">로그인</router-link>
      </p>
    </div>

    <!-- 가입 성공 모달 -->
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="closeSuccessModal">
      <div class="modal-content success-modal">
        <div class="success-icon">🎉</div>
        <h2>가입을 축하합니다!</h2>
        <div class="success-points">
          <div class="point-item">
            <span class="point-label">가입 보너스</span>
            <span class="point-value">+{{ formatPoints(receivedSignupBonus) }}P</span>
          </div>
          <div v-if="receivedReferralBonus > 0" class="point-item">
            <span class="point-label">추천인 보너스</span>
            <span class="point-value">+{{ formatPoints(receivedReferralBonus) }}P</span>
          </div>
          <div class="point-total">
            <span class="point-label">총 지급 포인트</span>
            <span class="point-value highlight">{{ formatPoints(totalReceivedPoints) }}P</span>
          </div>
        </div>
        <div class="my-referral-code">
          <p>나의 추천인 코드</p>
          <div class="code-box" @click="copyMyCode">
            <span>{{ myReferralCode }}</span>
            <button class="copy-btn">복사</button>
          </div>
          <p class="code-desc">친구에게 공유하면 서로 {{ formatPoints(settings.referrerBonus) }}P씩!</p>
        </div>
        <button @click="closeSuccessModal" class="btn-primary">시작하기</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useReferral } from '@/composables/useReferral'
import { usePointSettings } from '@/composables/usePointSettings'

export default {
  name: 'RegisterView',
  setup() {
    const router = useRouter()
    const { registerWithEmail, loginWithGoogle, error } = useAuth()
    const { validateReferralCode } = useReferral()
    const { settings, loadSettings, formatPoints } = usePointSettings()
    
    const email = ref('')
    const password = ref('')
    const passwordConfirm = ref('')
    const referralCode = ref('')
    const isLoading = ref(false)
    const localError = ref('')

    // 추천코드 검증 상태
    const referralChecking = ref(false)
    const referralValid = ref(false)
    const referralError = ref(false)
    const referralMessage = ref('')

    // 가입 성공 모달
    const showSuccessModal = ref(false)
    const myReferralCode = ref('')
    const receivedSignupBonus = ref(0)
    const receivedReferralBonus = ref(0)
    const totalReceivedPoints = ref(0)

    // 초기 설정 로드
    onMounted(async () => {
      await loadSettings()
    })

    // 추천코드 입력 처리 (자동 대문자)
    const handleReferralInput = () => {
      referralCode.value = referralCode.value.toUpperCase()
      // 입력 중에는 에러 상태 초기화
      if (referralCode.value.length < 6) {
        referralValid.value = false
        referralError.value = false
        referralMessage.value = ''
      }
    }

    // 추천코드 유효성 검증
    const validateReferralCodeInput = async () => {
      const code = referralCode.value.trim()
      
      if (!code) {
        referralValid.value = false
        referralError.value = false
        referralMessage.value = ''
        return
      }

      if (code.length !== 6) {
        referralValid.value = false
        referralError.value = true
        referralMessage.value = '추천코드는 6자리입니다.'
        return
      }

      referralChecking.value = true
      referralError.value = false
      referralMessage.value = ''

      try {
        const result = await validateReferralCode(code)
        referralValid.value = result.valid
        referralError.value = !result.valid
        referralMessage.value = result.message
      } catch (e) {
        referralError.value = true
        referralMessage.value = '확인 중 오류가 발생했습니다.'
      } finally {
        referralChecking.value = false
      }
    }

    // 이메일 회원가입
    const handleEmailRegister = async () => {
      localError.value = ''
      
      if (password.value !== passwordConfirm.value) {
        localError.value = '비밀번호가 일치하지 않습니다.'
        return
      }

      if (password.value.length < 6) {
        localError.value = '비밀번호는 6자 이상이어야 합니다.'
        return
      }

      // 추천코드가 있고 유효하지 않으면 경고
      if (referralCode.value && !referralValid.value && referralCode.value.length === 6) {
        localError.value = '유효하지 않은 추천코드입니다. 확인 후 다시 시도하세요.'
        return
      }

      isLoading.value = true
      try {
        const result = await registerWithEmail(
          email.value, 
          password.value, 
          referralValid.value ? referralCode.value : null
        )
        
        // 성공 모달 표시
        myReferralCode.value = result.myReferralCode
        receivedSignupBonus.value = result.signupBonus
        receivedReferralBonus.value = result.referralBonus
        totalReceivedPoints.value = result.signupBonus + result.referralBonus
        showSuccessModal.value = true

      } catch (e) {
        // 에러는 useAuth에서 처리
      } finally {
        isLoading.value = false
      }
    }

    // 구글 회원가입
    const handleGoogleRegister = async () => {
      isLoading.value = true
      try {
        const result = await loginWithGoogle(
          referralValid.value ? referralCode.value : null
        )
        
        if (result.isNewUser) {
          // 신규 가입자 - 성공 모달 표시
          myReferralCode.value = result.myReferralCode
          receivedSignupBonus.value = result.signupBonus
          receivedReferralBonus.value = result.referralBonus
          totalReceivedPoints.value = result.signupBonus + result.referralBonus
          showSuccessModal.value = true
        } else {
          // 기존 유저 - 바로 홈으로
          router.push('/')
        }
      } catch (e) {
        // 에러는 useAuth에서 처리
      } finally {
        isLoading.value = false
      }
    }

    // 내 추천코드 복사
    const copyMyCode = async () => {
      try {
        await navigator.clipboard.writeText(myReferralCode.value)
        alert('추천코드가 복사되었습니다!')
      } catch (e) {
        // 클립보드 API 지원 안되는 경우
        const textArea = document.createElement('textarea')
        textArea.value = myReferralCode.value
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        alert('추천코드가 복사되었습니다!')
      }
    }

    // 성공 모달 닫기
    const closeSuccessModal = () => {
      showSuccessModal.value = false
      router.push('/')
    }

    return {
      email,
      password,
      passwordConfirm,
      referralCode,
      error,
      localError,
      isLoading,
      referralChecking,
      referralValid,
      referralError,
      referralMessage,
      showSuccessModal,
      myReferralCode,
      receivedSignupBonus,
      receivedReferralBonus,
      totalReceivedPoints,
      settings,
      handleReferralInput,
      validateReferralCodeInput,
      handleEmailRegister,
      handleGoogleRegister,
      copyMyCode,
      closeSuccessModal,
      formatPoints
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.auth-container {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.auth-header {
  text-align: center;
  margin-bottom: 24px;
}

.auth-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.auth-subtitle {
  font-size: 15px;
  color: #6b7684;
}

/* 보너스 배너 */
.bonus-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%);
  border: 1px solid #c9e2fc;
  border-radius: 12px;
  margin-bottom: 24px;
}

.bonus-icon {
  font-size: 28px;
}

.bonus-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bonus-title {
  font-size: 13px;
  color: #6b7684;
}

.bonus-amount {
  font-size: 18px;
  font-weight: 700;
  color: #3182f6;
}

.btn-google {
  width: 100%;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.15s;
}

.btn-google:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  margin: 24px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e8eb;
}

.divider span {
  padding: 0 16px;
  font-size: 13px;
  color: #8b95a1;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 6px;
}

.label-optional {
  font-size: 12px;
  font-weight: 400;
  color: #8b95a1;
}

.input-group input {
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.15s;
}

.input-group input:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.input-group input::placeholder {
  color: #adb5bd;
}

.input-group input.input-success {
  border-color: #20c997;
  background: #f8fdfb;
}

.input-group input.input-error {
  border-color: #e03131;
  background: #fff8f8;
}

/* 추천코드 입력 */
.referral-input-wrapper {
  position: relative;
}

.referral-input-wrapper input {
  width: 100%;
  padding-right: 60px;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
}

.referral-status {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
}

.referral-status.checking {
  color: #8b95a1;
  font-size: 12px;
}

.referral-status.valid {
  color: #20c997;
  font-size: 18px;
}

.referral-status.invalid {
  color: #e03131;
  font-size: 18px;
}

.referral-message {
  font-size: 13px;
  margin: 0;
}

.referral-message.success {
  color: #20c997;
}

.referral-message.error {
  color: #e03131;
}

.referral-bonus {
  font-size: 13px;
  color: #3182f6;
  background: #f0f7ff;
  padding: 8px 12px;
  border-radius: 8px;
  margin: 4px 0 0 0;
}

.error-message {
  color: #e03131;
  font-size: 14px;
  text-align: center;
  margin: 0;
}

.btn-primary {
  padding: 16px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #1b64da;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-link {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #6b7684;
}

.auth-link a {
  color: #3182f6;
  font-weight: 600;
}

.auth-link a:hover {
  text-decoration: underline;
}

/* 성공 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  max-width: 400px;
  width: 100%;
  text-align: center;
}

.success-modal .success-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.success-modal h2 {
  font-size: 24px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 24px;
}

.success-points {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.point-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.point-item .point-label {
  font-size: 14px;
  color: #6b7684;
}

.point-item .point-value {
  font-size: 16px;
  font-weight: 600;
  color: #20c997;
}

.point-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid #e5e8eb;
}

.point-total .point-label {
  font-size: 14px;
  font-weight: 600;
  color: #191f28;
}

.point-total .point-value.highlight {
  font-size: 20px;
  font-weight: 700;
  color: #3182f6;
}

.my-referral-code {
  margin-bottom: 24px;
}

.my-referral-code > p:first-child {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 8px;
}

.code-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #f0f7ff;
  border: 2px dashed #3182f6;
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: all 0.15s;
}

.code-box:hover {
  background: #e5f1ff;
}

.code-box span {
  font-size: 24px;
  font-weight: 700;
  color: #3182f6;
  letter-spacing: 4px;
}

.copy-btn {
  padding: 6px 12px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.code-desc {
  font-size: 13px;
  color: #8b95a1;
  margin-top: 8px;
}

@media (max-width: 480px) {
  .auth-container {
    padding: 32px 24px;
  }

  .auth-title {
    font-size: 24px;
  }

  .modal-content {
    padding: 32px 24px;
  }

  .code-box span {
    font-size: 20px;
    letter-spacing: 2px;
  }
}
</style>